import React, { useCallback, useRef } from 'react'
import makeStyles from '@mui/styles/makeStyles'
import EditMessageProvider from 'src/feed/EditChatMessage/EditMessageProvider'
import { useMarkFeedUnreadFromMutation } from './feedQueries.generated'
import ScrollableFeedContainer from './ScrollableFeedContainer/ScrollableFeedContainer'
import useFeedItemsQuery from './useFeedItemsQuery'
import FeedItems from './FeedItems'
import SeeMoreButton from './FeedComponents/SeeMoreButton'
import { MentionResponseContextProvider } from './mentionResponse'
import { MentionLessonContextProvider } from './mentionLesson'
import useListenToLatestCoachMessages from './useListenToLatestCoachMessages'
import useMarkFeedAsSeen from './useMarkFeedAsSeen'
import useChatDisplayRules from './useChatDisplayRules'
import ChatMessageSendingSection from './ChatMessageSendingSection/ChatMessageSendingSection'
import { FeedContext } from './feedContext'

export const FEED_ITEM_COUNT_LIMIT = 50

type FeedProps = {
  feedID: string
}

const useStyles = makeStyles({
  feed: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
})

export default function ParticipantFeedCombined({ feedID }: FeedProps) {
  const styles = useStyles()

  const { hasOlder, isLoaded, items, isFetchingOlder, fetchMoreNewer, fetchMoreOlder, lastSeenAt, setLastSeenAt } =
    useFeedItemsQuery(feedID, FEED_ITEM_COUNT_LIMIT)

  // Set on the first unread item so opening the feed lands there rather than at the newest
  const firstUnreadRef = useRef<HTMLDivElement>(null)

  // `undefined` while the marker is still loading, `null` once it has resolved to no
  // boundary at all — a feed this coach has never opened, or one they have no association
  // with, where the server withholds the marker. Collapsing the two would leave the feed
  // waiting forever for a marker that is never coming.
  const unreadBoundaryKey = lastSeenAt === undefined ? undefined : lastSeenAt?.valueOf() ?? null

  // Fetch newer feed items when there are new chat messages
  useListenToLatestCoachMessages(feedID, fetchMoreNewer)

  // Mark as seen when content or feed changes
  const { withAutoMarkSeenPaused } = useMarkFeedAsSeen(feedID, items, isLoaded)

  const [markFeedUnreadFromMutation] = useMarkFeedUnreadFromMutation()

  // Rolls the last-seen marker back to just before the entry, moving the "New"
  // divider to it. Returning true keeps auto-mark-seen off for the rest of the
  // session, so the entry stays unread until the feed is reopened.
  const markAsUnread = useCallback(
    (createdAt: Date) =>
      withAutoMarkSeenPaused(async () => {
        const { data } = await markFeedUnreadFromMutation({ variables: { uid: feedID, from: createdAt } })
        const seenAt = data?.markFeedUnreadFrom.seenAt
        if (!seenAt) {
          return false
        }
        setLastSeenAt(seenAt)
        return true
      }),
    [feedID, markFeedUnreadFromMutation, setLastSeenAt, withAutoMarkSeenPaused]
  )

  const { disableChat, messageSubmitDisplayType } = useChatDisplayRules(feedID)

  return (
    <FeedContext.Provider value={{ fetchMoreNewer, markAsUnread }}>
      <div className={styles.feed}>
        <MentionResponseContextProvider feedID={feedID}>
          <MentionLessonContextProvider feedID={feedID}>
            <EditMessageProvider feedID={feedID}>
              {/* Keyed so scroll position and the initial landing reset per participant */}
              <ScrollableFeedContainer
                key={feedID}
                items={items}
                firstUnreadRef={firstUnreadRef}
                unreadBoundaryKey={unreadBoundaryKey}
              >
                {items.length > 0 && (
                  <>
                    {hasOlder && <SeeMoreButton fetchMoreOlder={fetchMoreOlder} isFetchingOlder={isFetchingOlder} />}
                    <FeedItems
                      items={items}
                      lastSeenAt={lastSeenAt}
                      hasOlder={hasOlder}
                      firstUnreadRef={firstUnreadRef}
                    />
                  </>
                )}
              </ScrollableFeedContainer>

              {!disableChat && (
                <ChatMessageSendingSection
                  feedID={feedID}
                  messageSubmitDisplayType={messageSubmitDisplayType}
                  messageCreatedCallback={fetchMoreNewer}
                />
              )}
            </EditMessageProvider>
          </MentionLessonContextProvider>
        </MentionResponseContextProvider>
      </div>
    </FeedContext.Provider>
  )
}
